// Customer 360 › Billing & delivery addresses.
// Shows the resolved billing address + every known delivery address (from past
// orders, the address book and the profile). Staff can add new addresses or
// edit/remove ones they added — imported records are never changed.
import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Pencil, Plus, Receipt, Star, Trash2, Truck } from "lucide-react";
import AddressForm from "./AddressForm";
import { addressLines } from "../../lib/addressBook";

function AddressItem({ a, kind, primary, onEdit, onRemove, onMakeDefault, readOnly }) {
  const lines = addressLines(a);
  return (
    <div className={`rounded-md border px-2.5 py-2 ${primary ? "border-emerald-500/30 bg-emerald-500/[0.04]" : "border-white/[0.06] bg-white/[0.02]"}`}
      data-testid={`addr-${kind}-${a.id}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {(a.label || a.company || a.name) && (
            <div className="text-[12px] font-semibold text-white truncate">{a.label || a.company || a.name}</div>
          )}
          {a.label && (a.company || a.name) && <div className="text-[11px] text-slate-400 truncate">{[a.name, a.company].filter(Boolean).join(" · ")}</div>}
          <div className="text-[11.5px] text-slate-200 leading-snug">{lines.join(", ")}</div>
          <div className="mt-1 flex items-center gap-1.5 flex-wrap">
            {primary && <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-emerald-500/40 text-emerald-300">{kind === "billing" ? "Billing" : "Default"}</span>}
            <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${a.saved ? "border-orange-500/30 text-orange-300" : "border-white/10 text-slate-500"}`}>{a.source}</span>
          </div>
        </div>
        {!readOnly && <div className="flex items-center gap-0.5 shrink-0">
          {!primary && (
            <button type="button" onClick={onMakeDefault} title={a.saved ? "Make default" : "Save a copy as the default"}
              className="p-1 rounded text-slate-500 hover:text-amber-300 hover:bg-white/5" data-testid={`addr-${kind}-default-${a.id}`}><Star className="w-3 h-3" /></button>
          )}
          <button type="button" onClick={onEdit} title={a.saved ? "Edit" : "Edit as a new saved address (original kept)"}
            className="p-1 rounded text-slate-500 hover:text-white hover:bg-white/5" data-testid={`addr-${kind}-edit-${a.id}`}><Pencil className="w-3 h-3" /></button>
          {a.saved && (
            <button type="button" onClick={onRemove} title="Remove"
              className="p-1 rounded text-slate-500 hover:text-rose-300 hover:bg-white/5" data-testid={`addr-${kind}-remove-${a.id}`}><Trash2 className="w-3 h-3" /></button>
          )}
        </div>}
      </div>
    </div>
  );
}

function Section({ kind, icon: Icon, title, items, primaryId, book, readOnly }) {
  const [editing, setEditing] = useState(null); // null | "new" | address
  const [showAll, setShowAll] = useState(false);
  const save = async (form, base) => {
    try {
      await book.save(kind, { ...form, saved: !!base?.saved, id: base?.saved ? base.id : undefined });
      toast.success("Address saved");
      setEditing(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save address"); }
  };
  const remove = async (a) => {
    if (!window.confirm("Remove this saved address?")) return;
    try { await book.remove(a.id); toast.success("Address removed"); } catch { toast.error("Could not remove"); }
  };
  const visible = showAll ? items : items.slice(0, kind === "billing" ? 1 : 4);
  return (
    <div className="space-y-1.5" data-testid={`addr-section-${kind}`}>
      <div className="flex items-center justify-between">
        <span className="text-[9.5px] font-mono uppercase tracking-widest text-slate-400 inline-flex items-center gap-1.5">
          <Icon className="w-3 h-3" /> {title} <span className="text-slate-600">{items.length}</span>
        </span>
        {!readOnly && <button type="button" onClick={() => setEditing("new")} data-testid={`addr-${kind}-add`}
          className="text-[10.5px] font-mono text-orange-300 hover:text-orange-200 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>}
      </div>
      {editing === "new" && <AddressForm kind={kind} testid={`addr-${kind}-form`} onCancel={() => setEditing(null)} onSave={(f) => save(f, null)} />}
      {items.length === 0 && editing !== "new" && (
        <div className="text-[11px] text-slate-500 font-mono py-2 text-center border border-dashed border-white/[0.06] rounded">No {kind} address yet.</div>
      )}
      {visible.map((a) => editing && editing !== "new" && editing.id === a.id ? (
        <AddressForm key={a.id} kind={kind} initial={a} testid={`addr-${kind}-form`} onCancel={() => setEditing(null)} onSave={(f) => save(f, a)} />
      ) : (
        <AddressItem key={a.id} a={a} kind={kind} primary={a.id === primaryId} readOnly={readOnly}
          onEdit={() => setEditing(a)} onRemove={() => remove(a)}
          onMakeDefault={() => save({ ...a, is_default: true }, a)} />
      ))}
      {items.length > visible.length && (
        <button type="button" onClick={() => setShowAll(true)} data-testid={`addr-${kind}-more`}
          className="w-full text-[10.5px] font-mono text-slate-400 hover:text-white py-1">Show {items.length - visible.length} more</button>
      )}
      {showAll && items.length > (kind === "billing" ? 1 : 4) && (
        <button type="button" onClick={() => setShowAll(false)} className="w-full text-[10.5px] font-mono text-slate-500 hover:text-white py-1">Show less</button>
      )}
    </div>
  );
}

export default function AddressBookPanel({ book, readOnly = false }) {
  const d = book.data;
  if (!d) return <div className="px-3 py-3 text-[11px] text-slate-500 font-mono">Loading addresses…</div>;
  return (
    <div className="px-3 py-3 space-y-4" data-testid="address-book-panel">
      <Section kind="billing" icon={Receipt} title="Billing address" items={d.billing_options || []} primaryId={d.billing?.id} book={book} readOnly={readOnly} />
      <Section kind="delivery" icon={Truck} title="Delivery addresses" items={d.delivery || []} primaryId={d.default_delivery_id} book={book} readOnly={readOnly} />
      <p className="text-[10px] text-slate-600 font-mono flex items-start gap-1">
        <MapPin className="w-3 h-3 shrink-0 mt-px" /> Pulled from past orders, the address book &amp; profile. Imported records are never changed. Edits are saved as new addresses.
      </p>
    </div>
  );
}
